/**
 * apiConfig.js — Centralized API endpoint configuration
 *
 * The portal at https://www.enablegames.xyz is the API.
 *
 * For development only — a page served from this machine (localhost, 127.0.0.1) — the
 * address can be overridden: pass ?api=http://localhost:2505 in the URL query string
 * (kept in localStorage so navigation does not lose it), or set window.__API_BASE__
 * before the app loads. With no override such a page uses the local backend at
 * http://localhost:2505 (production's CORS only allows https://www.enablegames.xyz).
 *
 * Anywhere else the override is ignored and a stored one is deleted: a link such as
 * https://www.enablegames.xyz/?api=https://evil.example would otherwise send every
 * later sign-in (email and password) and every API call (the token) to that host.
 */

const PRODUCTION_URL = 'https://www.enablegames.xyz';
const LOCAL_URL = 'http://localhost:2505';
const STORAGE_KEY = 'enablegames_api_base';

/** Whether the page is served from this machine, where the override is a developer's. */
export function isLocalPage(hostname) {
    const h = String(hostname || '').toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1' || h.endsWith('.localhost');
}

function browserStorage() {
    try { return window.localStorage; } catch (e) { return null; }
}

/**
 * The API base for a page at `location`. The arguments default to the browser's;
 * the tests pass their own.
 */
export function resolveApiBase(location = window.location, storage = browserStorage(), globals = window) {
    if (!isLocalPage(location.hostname)) {
        // A stored override on the real site can only have come from a ?api= link: drop it.
        try { if (storage) storage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
        return PRODUCTION_URL;
    }

    // 1. Query string override: ?api=http://localhost:2505
    try {
        const apiOverride = new URLSearchParams(location.search).get('api');
        if (apiOverride) {
            console.log('[apiConfig] Using query string override:', apiOverride);
            // Persist it so navigation doesn't lose it
            if (storage) storage.setItem(STORAGE_KEY, apiOverride);
            return apiOverride.replace(/\/+$/, '');
        }
    } catch (e) { /* ignore */ }

    // 2. localStorage override (persisted from the query string or set by hand)
    try {
        const stored = storage && storage.getItem(STORAGE_KEY);
        if (stored) {
            console.log('[apiConfig] Using stored API base:', stored);
            return stored.replace(/\/+$/, '');
        }
    } catch (e) { /* ignore */ }

    // 3. Global override
    if (globals && globals.__API_BASE__) {
        return globals.__API_BASE__.replace(/\/+$/, '');
    }

    // 4. A dev server on this machine talks to the local backend
    return LOCAL_URL;
}

export const API_BASE = resolveApiBase();

/** The portal this page talks to, as a host ("www.enablegames.xyz", "localhost:2505"); '' if unreadable. */
export function apiHost(base = API_BASE) {
    try { return new URL(base).host; } catch (e) { return ''; }
}

/**
 * Helper to clear the stored override and revert to auto-detection.
 * Call this from the browser console: import('/src/apiConfig.js').then(m => m.clearApiOverride())
 */
export function clearApiOverride() {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[apiConfig] Cleared API override. Reload to apply.');
}
